import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Icon } from './icon';

export const TOAST_DURATION_MS = 4000;

type Notify = (message: string) => void;

const ToastCtx = createContext<Notify>(() => undefined);

/**
 * Short confirmation after an action ("Project created", FR-PRJ-001) — one live region for the
 * whole app, mounted once at the root. Toasts are informational and click-through (they never
 * steal focus or close an open dialog) and disappear on their own.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<{ id: number; message: string }[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Set<number>());

  const notify = useCallback<Notify>((message) => {
    const id = (nextId.current += 1);
    setItems((list) => [...list, { id, message }]);
    const timer = window.setTimeout(() => {
      timers.current.delete(timer);
      setItems((list) => list.filter((item) => item.id !== id));
    }, TOAST_DURATION_MS);
    timers.current.add(timer);
  }, []);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((timer) => window.clearTimeout(timer));
  }, []);

  return (
    <ToastCtx.Provider value={notify}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col items-end gap-2"
      >
        {items.map((item) => (
          <div
            key={item.id}
            role="status"
            className="flex max-w-sm items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm text-white shadow-lg"
          >
            <Icon name="check" className="size-4 shrink-0 text-emerald-400" />
            {item.message}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = (): Notify => useContext(ToastCtx);
